import {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import * as css from './SeatmapManage.css';

// Colors for resolved seat state. Free seats use their category color.
const STATE_FILL = {blocked: '#9ca3af', hidden: '#e5e7eb', sold: '#111827', reserved: '#f59e0b'};
const seatFill = (s) => STATE_FILL[s.state] || s.color || '#93c5fd';

// Base seat layer — 8k+ circles. Rendered ONCE (memo on dataVersion); pan/zoom
// only change the <svg> viewBox (no re-render), selection lives in an overlay.
const SeatLayer = memo(function SeatLayer({seats, r}) {
    return (
        <g>
            {seats.map((s) => (
                <circle key={s.code} data-code={s.code} cx={s.x} cy={s.y} r={r}
                    fill={seatFill(s)}
                    fillOpacity={s.state === 'hidden' ? 0.35 : 1}
                    stroke={s.state === 'hidden' ? '#9ca3af' : 'none'}
                    strokeDasharray={s.state === 'hidden' ? '2 2' : undefined}
                    strokeWidth={s.state === 'hidden' ? r * 0.15 : 0} />
            ))}
        </g>
    );
}, (a, b) => a.dataVersion === b.dataVersion && a.r === b.r);

// Row-number labels only (one per row, at the row's end). Seat-sized (world
// units) so they scale with zoom. Memoized so pan/zoom never re-renders them.
const LabelLayer = memo(function LabelLayer({rowLabels, r}) {
    return (
        <g pointerEvents="none" fill="#334155" fontWeight="700" fontFamily="inherit">
            {rowLabels.map((l, i) => (
                <text key={'r' + i} x={l.x + r * 2.6} y={l.y} fontSize={r * 2}
                    textAnchor="start" dominantBaseline="central">{l.row}</text>
            ))}
        </g>
    );
}, (a, b) => a.dataVersion === b.dataVersion && a.r === b.r);

function SeatCanvas({seats, bounds, dataVersion, selected, setSelected, mode, highlightCodes, areas, onSelectArea, onHoverArea}) {
    const svgRef = useRef(null);
    const wrapRef = useRef(null);

    // seat radius from the ACTUAL seat pitch (nearest-neighbour), not the global
    // average — seats cluster in sections with big gaps between them, so the
    // average overestimates and makes circles overlap. Leave a real gap.
    const r = useMemo(() => {
        const n = seats.length;
        if(n < 2) return 8;
        const sampleN = Math.min(160, n);
        const step = Math.max(1, Math.floor(n / sampleN));
        const dists = [];
        for(let i = 0; i < n; i += step) {
            const s = seats[i];
            let min = Infinity;
            for(let j = 0; j < n; j++) {
                if(j === i) continue;
                const dx = seats[j].x - s.x, dy = seats[j].y - s.y;
                const d2 = dx * dx + dy * dy;
                if(d2 < min) min = d2;
            }
            if(min < Infinity && min > 0) dists.push(Math.sqrt(min));
        }
        dists.sort((a, b) => a - b);
        const pitch = dists[Math.floor(dists.length / 2)] || 20;
        return Math.max(1.5, pitch * 0.36); // ~28% gap between neighbouring seats
    }, [seats]);

    // section (gush) centroids for on-map labels + whole-gush selection
    const areaTitle = useMemo(() => {
        const m = {}; (areas || []).forEach((a) => { m[a.smaid] = a.title; }); return m;
    }, [areas]);
    const centroids = useMemo(() => {
        const acc = {};
        for(const s of seats) {
            const a = acc[s.smaid] || (acc[s.smaid] = {sx: 0, sy: 0, n: 0});
            a.sx += s.x; a.sy += s.y; a.n++;
        }
        return Object.entries(acc).map(([smaid, a]) => ({smaid: Number(smaid), x: a.sx / a.n, y: a.sy / a.n}));
    }, [seats]);

    // Row-number labels (one per row, at the row's end) + seat-number labels
    // (across each section's top row) — so the row/seat structure is legible.
    const rowLabels = useMemo(() => {
        const g = {}; // smaid|row -> extreme seats (min & max x) + y
        for(const s of seats) {
            if(s.row == null || s.row === '') continue;
            const k = s.smaid + '|' + s.row;
            const e = g[k] || (g[k] = {row: s.row, loX: s, hiX: s});
            if(s.x < e.loX.x) e.loX = s;
            if(s.x > e.hiX.x) e.hiX = s;
        }
        // place the label just past the row's RIGHT-most seat
        return Object.values(g).map((e) => ({row: e.row, x: e.hiX.x, y: e.hiX.y}));
    }, [seats]);

    const pad = r * 6;
    const initialVB = useMemo(() => ({
        x: bounds.minX - pad, y: bounds.minY - pad,
        w: (bounds.maxX - bounds.minX) + pad * 2, h: (bounds.maxY - bounds.minY) + pad * 2
    }), [bounds, pad]);
    const [vb, setVb] = useState(initialVB);
    useEffect(() => { setVb(initialVB); }, [initialVB]);

    const seatByCode = useMemo(() => {
        const m = new Map(); seats.forEach((s) => m.set(s.code, s)); return m;
    }, [seats]);

    // seat NUMBERS drawn ON the circles, only once zoomed in enough to be
    // readable (like the buyer form). Viewport-culled so we never render 8k texts.
    const [svgW, setSvgW] = useState(1000);
    useEffect(() => {
        const el = svgRef.current;
        if(!el || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(() => setSvgW(el.clientWidth || 1000));
        ro.observe(el); setSvgW(el.clientWidth || 1000);
        return () => ro.disconnect();
    }, []);
    const screenR = r * (svgW / vb.w);          // on-screen seat radius (px)
    const showSeatNums = screenR >= 8;
    const visibleSeatNums = useMemo(() => {
        if(!showSeatNums) return [];
        const mx = vb.w * 0.03, my = vb.h * 0.03;
        const x0 = vb.x - mx, x1 = vb.x + vb.w + mx, y0 = vb.y - my, y1 = vb.y + vb.h + my;
        const out = [];
        for(const s of seats) {
            if(s.seat == null || s.seat === '') continue;
            if(s.x >= x0 && s.x <= x1 && s.y >= y0 && s.y <= y1) { out.push(s); if(out.length > 2500) break; }
        }
        return out;
    }, [showSeatNums, vb, seats]);

    // pixel -> world. The svg uses preserveAspectRatio="xMidYMid meet", so the
    // viewBox is scaled UNIFORMLY and CENTERED (letterboxed) inside the element —
    // a naive linear map anchors selection at the wrong point. Account for the
    // uniform scale + centering offset.
    const toWorld = useCallback((clientX, clientY) => {
        const rect = svgRef.current.getBoundingClientRect();
        const scale = Math.min(rect.width / vb.w, rect.height / vb.h);
        const offX = (rect.width - vb.w * scale) / 2;
        const offY = (rect.height - vb.h * scale) / 2;
        return {
            x: vb.x + (clientX - rect.left - offX) / scale,
            y: vb.y + (clientY - rect.top - offY) / scale
        };
    }, [vb]);

    // ---- wheel zoom (around cursor) ----
    const onWheel = useCallback((e) => {
        e.preventDefault();
        const factor = e.deltaY > 0 ? 1.12 : 1 / 1.12;
        const p = toWorld(e.clientX, e.clientY);
        setVb((v) => {
            const nw = Math.min(initialVB.w * 3, Math.max(r * 20, v.w * factor));
            const nh = nw * (v.h / v.w);
            return {x: p.x - (p.x - v.x) * (nw / v.w), y: p.y - (p.y - v.y) * (nh / v.h), w: nw, h: nh};
        });
    }, [toWorld, initialVB.w, r]);
    useEffect(() => {
        const el = wrapRef.current;
        if(!el) return;
        el.addEventListener('wheel', onWheel, {passive: false});
        return () => el.removeEventListener('wheel', onWheel);
    }, [onWheel]);

    // ---- drag: box-select (select mode) or pan (pan mode / space) ----
    const drag = useRef(null);
    const suppressClick = useRef(false);
    const [box, setBox] = useState(null); // world-space selection rect preview
    const [space, setSpace] = useState(false);
    useEffect(() => {
        const d = (e) => { if(e.code === 'Space') setSpace(true); };
        const u = (e) => { if(e.code === 'Space') setSpace(false); };
        window.addEventListener('keydown', d); window.addEventListener('keyup', u);
        return () => { window.removeEventListener('keydown', d); window.removeEventListener('keyup', u); };
    }, []);

    const panning = () => mode === 'pan' || space;

    const onPointerDown = (e) => {
        if(e.button !== 0) return;
        svgRef.current.setPointerCapture(e.pointerId);
        const rect = svgRef.current.getBoundingClientRect();
        const scale = Math.min(rect.width / vb.w, rect.height / vb.h);
        const w = toWorld(e.clientX, e.clientY);
        drag.current = {startX: e.clientX, startY: e.clientY, wx: w.x, wy: w.y, shift: e.shiftKey, moved: false, pan: panning(), vb, scale};
    };
    const onPointerMove = (e) => {
        const d = drag.current;
        if(!d) return;
        if(Math.abs(e.clientX - d.startX) + Math.abs(e.clientY - d.startY) > 3) d.moved = true;
        if(d.pan) {
            const dx = (e.clientX - d.startX) / d.scale;
            const dy = (e.clientY - d.startY) / d.scale;
            setVb({...d.vb, x: d.vb.x - dx, y: d.vb.y - dy});
        } else {
            const w = toWorld(e.clientX, e.clientY);
            setBox({x: Math.min(d.wx, w.x), y: Math.min(d.wy, w.y), w: Math.abs(w.x - d.wx), h: Math.abs(w.y - d.wy)});
        }
    };
    const onPointerUp = (e) => {
        const d = drag.current; drag.current = null;
        try { svgRef.current.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
        if(!d) return;
        if(d.pan) { setBox(null); if(d.moved) suppressClick.current = true; return; }
        if(!d.moved) { setBox(null); return; } // a click (handled by seat onClick)
        suppressClick.current = true; // a box-drag just ran — swallow the trailing click
        const w = toWorld(e.clientX, e.clientY);
        const x0 = Math.min(d.wx, w.x), x1 = Math.max(d.wx, w.x);
        const y0 = Math.min(d.wy, w.y), y1 = Math.max(d.wy, w.y);
        const hit = [];
        for(const s of seats) if(s.x >= x0 && s.x <= x1 && s.y >= y0 && s.y <= y1) hit.push(s.code);
        setSelected((prev) => {
            const next = d.shift ? new Set(prev) : new Set();
            hit.forEach((c) => next.add(c));
            return next;
        });
        setBox(null);
    };

    const onSeatClick = (e) => {
        if(suppressClick.current) { suppressClick.current = false; return; }
        if(panning()) return;
        const code = e.target.getAttribute('data-code');
        if(!code || !seatByCode.has(code)) return;
        e.stopPropagation();
        setSelected((prev) => {
            const next = new Set(prev);
            if(next.has(code)) next.delete(code); else next.add(code);
            return next;
        });
    };

    // double-click a seat → select its whole row (same section + row label)
    const onSeatDblClick = (e) => {
        const code = e.target.getAttribute('data-code');
        if(!code || !seatByCode.has(code)) return;
        e.stopPropagation();
        const s = seatByCode.get(code);
        const rowCodes = seats.filter((x) => x.smaid === s.smaid && x.row === s.row).map((x) => x.code);
        setSelected((prev) => {
            const next = e.shiftKey ? new Set(prev) : new Set();
            rowCodes.forEach((c) => next.add(c));
            return next;
        });
    };

    const selArr = useMemo(() => Array.from(selected).map((c) => seatByCode.get(c)).filter(Boolean), [selected, seatByCode]);
    const hlArr = useMemo(() => (highlightCodes || []).map((c) => seatByCode.get(c)).filter(Boolean), [highlightCodes, seatByCode]);

    return (
        <css.canvasWrap ref={wrapRef} $pan={panning()}>
            <svg ref={svgRef} width="100%" height="100%"
                viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`}
                preserveAspectRatio="xMidYMid meet"
                onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}
                onClickCapture={onSeatClick} onDoubleClickCapture={onSeatDblClick}>
                {/* invisible larger hit targets for click (thin overlay per seat is too heavy;
                    rely on the base circles which carry data-code) */}
                <SeatLayer seats={seats} r={r} dataVersion={dataVersion} />
                <LabelLayer rowLabels={rowLabels} r={r} dataVersion={dataVersion} />
                {/* seat numbers ON the circles (zoomed-in only, viewport-culled) */}
                {showSeatNums &&
                    <g pointerEvents="none" fill="#ffffff" stroke="#00000055" paintOrder="stroke"
                        strokeWidth={r * 0.07} fontWeight="700" style={{userSelect: 'none'}}>
                        {visibleSeatNums.map((s) => (
                            <text key={'sn' + s.code} x={s.x} y={s.y}
                                fontSize={r * Math.min(0.95, 1.7 / Math.max(1, String(s.seat).length))}
                                textAnchor="middle" dominantBaseline="central">{s.seat}</text>
                        ))}
                    </g>}
                {/* highlight (a ticket's seats) — amber halo */}
                <g pointerEvents="none">
                    {hlArr.map((s) => (
                        <circle key={s.code} cx={s.x} cy={s.y} r={r * 1.15}
                            fill="none" stroke="#f59e0b" strokeWidth={r * 0.35} />
                    ))}
                </g>
                {/* selection — blue ring */}
                <g pointerEvents="none">
                    {selArr.map((s) => (
                        <circle key={s.code} cx={s.x} cy={s.y} r={r * 1.35}
                            fill="none" stroke="#2563eb" strokeWidth={r * 0.4} />
                    ))}
                </g>
                {/* section (gush) labels — click to select the whole gush */}
                <g>
                    {centroids.map((c) => {
                        const title = areaTitle[c.smaid] || '';
                        const fs = vb.w * 0.014;
                        const w = Math.max(fs * 2.4, title.length * fs * 0.62 + fs);
                        const h = fs * 1.7;
                        return (
                            <g key={c.smaid} style={{cursor: 'pointer'}}
                                onClick={(e) => { e.stopPropagation(); if(onSelectArea) onSelectArea(c.smaid, e.shiftKey); }}
                                onMouseEnter={() => onHoverArea && onHoverArea(c.smaid)}
                                onMouseLeave={() => onHoverArea && onHoverArea(null)}>
                                <rect x={c.x - w / 2} y={c.y - h / 2} width={w} height={h} rx={h * 0.35}
                                    fill="#0f172a" fillOpacity={0.66} stroke="#ffffff" strokeOpacity={0.25} strokeWidth={fs * 0.05} />
                                <text x={c.x} y={c.y} fontSize={fs} fill="#fff" fontWeight="700"
                                    textAnchor="middle" dominantBaseline="central" style={{userSelect: 'none'}}>{title}</text>
                            </g>
                        );
                    })}
                </g>
                {box && <rect x={box.x} y={box.y} width={box.w} height={box.h}
                    fill="#2563eb22" stroke="#2563eb" strokeWidth={r * 0.2} pointerEvents="none" />}
            </svg>
        </css.canvasWrap>
    );
}

export default SeatCanvas;
